/* @flow */
import cx from 'classnames';
import * as React from 'react';
import PropTypes from 'prop-types';

import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar';
import MessageItemContent from './MessageItemContent';
import Text from 'components/Text';
import ReactionDisplay from 'chat/components/ReactionDisplay';
import ReactionStatus from 'chat/components/ReactionStatus';
import MessageSenderAvatarContainer from 'chat/components/MessageSenderAvatarContainer';
import { ReactionContainer } from 'chat/components/Reaction';
import ModalTrigger from 'components/ModalTrigger';
import RelationshipModal from 'user/components/RelationshipModal';
import UserLink from 'users/components/UserLink';
import Timestamp from 'components/Timestamp';

import './styles/AnnouncementMessageItem.scss';

import ChatMessage from 'entities/ChatMessage';
import Message from 'entities/Message';

type Props = {
  message: ChatMessage | Message,
  group: Group,
  subgroupRole?: ?string,
  senderInitials: string,
  showProfilePic: boolean,
  lastOfGroup: boolean,
  disableReactionToggle?: boolean,
  showMetadata?: boolean,
  isGroupChat?: boolean,
};

type State = {
  reactionPickerVisible: boolean,
  hovered: boolean,
};

function getStreamAndMessagePair(message: *) {
  try {
    if (message instanceof ChatMessage) {
      return {
        streamUuid: message.getChatId(),
        messageUuid: message.getUuid(),
      };
    }

    if (message instanceof Message) {
      return {
        streamUuid: message.getGroupThreads()[0].uuid,
        messageUuid: message.getUuid(),
      };
    }
  } catch (e) {
    console.error(e);
  }

  return {
    streamUuid: null,
    messageUuid: null,
  };
}

// Deprecated: see /Stream/components/.../AnnouncementMessageItem for newest version.
// Only used in the PeopleListGroupProfile and the admin overview tab, to display
// recent announcements.
export class AnnouncementMessageItem extends React.PureComponent<Props, State> {
  state: State = {
    reactionPickerVisible: false,
    hovered: false,
  };

  static contextTypes = {
    threadUUID: PropTypes.string,
  };

  handleHoverReactionPickerShow = () => {
    this.setState({ reactionPickerVisible: true });
  };

  handleHoverReactionPickerHide = () => {
    this.setState({ reactionPickerVisible: false });
  };

  handleEnterHover = () => {
    this.setState({ hovered: true });
  };

  handleLeaveHover = () => {
    this.setState({ hovered: false });
  };

  getThreadContext = (): ?string => {
    return this.context.threadUUID;
  };

  renderAnnouncementHeader() {
    const { group } = this.props;

    return (
      <div className="AnnouncementMessageItem-head">
        <GroupAvatar className="AnnouncementMessageItem-announcementBadge" group={group} />
        <div className="AnnouncementMessageItem-head-classInfo">
          <Text className="AnnouncementMessageItem-groupName" weight="semibold" truncate>
            {group.getName()}
          </Text>
        </div>
      </div>
    );
  }

  renderMetadata() {
    const { message, isGroupChat } = this.props;
    const sender = message.getSender();
    if (!sender) {
      return null;
    }

    const timestamp = (
      <span>
        {' • '}
        <Timestamp date={message.getSentAt()} format="short" />
      </span>
    );

    if (sender.id) {
      const modal = <RelationshipModal userId={sender.id} />;

      return (
        <div className="AnnouncementMessageItem-metadata">
          <ModalTrigger modal={modal}>
            <UserLink userId={sender.id} neverShowStartChat={!isGroupChat} color="inherit">
              <span className="AnnouncementMessageItem-metadata-link">{sender.getName()}</span>
            </UserLink>
          </ModalTrigger>
          {timestamp}
        </div>
      );
    }

    return (
      <span className="AnnouncementMessageItem-metadata">
        {sender.getName()}
        {timestamp}
      </span>
    );
  }

  renderReactionStatus() {
    const { message } = this.props;
    const { streamUuid, messageUuid } = getStreamAndMessagePair(message);

    if (!streamUuid || !messageUuid) {
      console.error('annoucement message item: could not render reactions status');
      return null;
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionStatus
            message={message}
            reactionSummary={reactionSummary}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  renderReactions() {
    const { message, disableReactionToggle } = this.props;
    const { streamUuid, messageUuid } = getStreamAndMessagePair(message);

    if (!streamUuid || !messageUuid) {
      console.error('annoucement message item: could not render reactions');
      return null;
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionDisplay
            reactionSummary={reactionSummary}
            disableReactionToggle={!!disableReactionToggle}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  renderDroplet() {
    const { lastOfGroup, message, showProfilePic } = this.props;
    const sender = message.getSender();

    if (!sender) {
      console.error('AnnouncementMessageItem renderDroplet must have a message sender');
      return null;
    }

    const senderUuid = sender.getUuid();
    return lastOfGroup && showProfilePic ? (
      <span className="AnnouncementMessageItem-droplet">
        <MessageSenderAvatarContainer uuid={senderUuid} />
      </span>
    ) : null;
  }

  render() {
    const { message, showMetadata } = this.props;
    const { hovered, reactionPickerVisible } = this.state;

    return (
      <div
        className={cx('AnnouncementMessageItem', 'AnnouncementMessageItem-other', {
          'AnnouncementMessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="AnnouncementMessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}>
          <div className="AnnouncementMessageItem-column">
            {showMetadata && this.renderMetadata()}
            <div className="AnnouncementMessageItem-dropletChubble">
              <div className="AnnouncementMessageItem-chubble">
                {this.renderAnnouncementHeader()}
                <MessageItemContent className="AnnouncementMessageItem-content" message={message} />
              </div>
              {this.renderDroplet()}
            </div>
            {message.canReact() && this.renderReactionStatus()}
          </div>
        </div>
        {message.canReact() && this.renderReactions()}
      </div>
    );
  }
}

export default AnnouncementMessageItem;
